import { createSelector } from 'reselect';

import { settingsOptionsDaiDefaultValue } from '../../domain/dai/settingsOptionsDaiDefaultValue';
import { SettingsOptionsDai } from '../../domain/dai/types';
import { LoadingState } from '../../domain/schemas';

import { RootState } from '../../store';
import {
  unitDataListReportsDataViewModeSelector,
  unitDataListReportsSearchConditionSelector,
} from '../ui/unitDataListReportsSetting';

// Action Types

const FETCH_SETTINGS_OPTIONS_DAI = 'FETCH_SETTINGS_OPTIONS_DAI' as const;
const FETCH_SETTINGS_OPTIONS_DAI_REQUEST =
  'FETCH_SETTINGS_OPTIONS_DAI_REQUEST' as const;
const FETCH_SETTINGS_OPTIONS_DAI_SUCCESS =
  'FETCH_SETTINGS_OPTIONS_DAI_SUCCESS' as const;
const RENEW_SETTINGS_OPTIONS_DAI = 'RENEW_SETTINGS_OPTIONS_DAI' as const;

export const SettingsOptionsDaiActionTypes = {
  FETCH_SETTINGS_OPTIONS_DAI,
  FETCH_SETTINGS_OPTIONS_DAI_REQUEST,
  FETCH_SETTINGS_OPTIONS_DAI_SUCCESS,
  RENEW_SETTINGS_OPTIONS_DAI,
};

// Action Creators

function fetchSettingsOptionsDaiAction() {
  return {
    type: FETCH_SETTINGS_OPTIONS_DAI,
  };
}

function fetchSettingsOptionsDaiRequestAction() {
  return {
    type: FETCH_SETTINGS_OPTIONS_DAI_REQUEST,
  };
}

function fetchSettingsOptionsDaiSuccessAction(
  settingsOptionsDai: SettingsOptionsDai
) {
  return {
    type: FETCH_SETTINGS_OPTIONS_DAI_SUCCESS,
    payload: { settingsOptionsDai },
  };
}

function renewSettingsOptionsDaiAction() {
  return {
    type: RENEW_SETTINGS_OPTIONS_DAI,
  };
}

export const SettingsOptionsDaiActionCreators = {
  fetchSettingsOptionsDaiAction,
  fetchSettingsOptionsDaiRequestAction,
  fetchSettingsOptionsDaiSuccessAction,
  renewSettingsOptionsDaiAction,
};

// Actions

type FetchSettingsOptionsDaiAction = ReturnType<
  typeof fetchSettingsOptionsDaiAction
>;

type SettingsOptionsDaiAction =
  | FetchSettingsOptionsDaiAction
  | ReturnType<typeof fetchSettingsOptionsDaiRequestAction>
  | ReturnType<typeof fetchSettingsOptionsDaiSuccessAction>
  | ReturnType<typeof renewSettingsOptionsDaiAction>;

// State

type SettingsOptionsDaiState = {
  loadingState: LoadingState;
  settingsOptionsDai?: SettingsOptionsDai;
};

const initialState: SettingsOptionsDaiState = {
  loadingState: 'prepare',
  settingsOptionsDai: undefined,
};

// Selector

/**
 *  settingsOptionsを全て取得する
 */
export const settingsOptionsDaiSelector = (state: RootState) => {
  const settings = state.settingsOptionsDai.settingsOptionsDai;
  // MEMO: コンポーネント側がundefined許容しないため、空の場合空データを返す
  return settings ? settings : settingsOptionsDaiDefaultValue;
};

// 現在のローディング状態を取得
export const settingsOptionsDaiLoadingStateSelector = (state: RootState) => {
  return state.settingsOptionsDai.loadingState;
};

// SearchConditionだけ取得する
export const settingsOptionsDaiSearchConditionSelector = createSelector(
  settingsOptionsDaiSelector,
  (settingsOptionsDai) => {
    // MEMO: コンポーネント側がundefined許容しないため、空の場合空データを返す
    if (settingsOptionsDai === undefined)
      return settingsOptionsDaiDefaultValue.searchCondition;

    return settingsOptionsDai.searchCondition;
  }
);

/**
 * フィルタ-用のジャンル一覧を取得する
 * @returns フィルタ-用のジャンル一覧
 */
export const settingsOptionsDaiGenresSelector = createSelector(
  settingsOptionsDaiSelector,
  (settingsOptionsDai) => {
    // MEMO: コンポーネント側がundefined許容しないため、空の場合空データを返す
    if (settingsOptionsDai === undefined)
      return settingsOptionsDaiDefaultValue.genres;

    return settingsOptionsDai.genres;
  }
);

// 表示項目として表示する項目を取得
export const settingsOptionsDaiFieldsSelector = createSelector(
  settingsOptionsDaiSelector,
  (settingsOptionsDai) => settingsOptionsDai.fields
);

/**
 * 台別データ表示項目のFieldsのisNewがtrueのものがあるかどうかを取得する
 * （表示区分（標準/出玉ランキング）に応じて対象の表示項目セットを切り替える）
 */
export const settingsOptionsDaiFieldsIsNewSelector = createSelector(
  settingsOptionsDaiFieldsSelector,
  unitDataListReportsDataViewModeSelector,
  (fields, dataViewMode) => {
    const targetFields =
      dataViewMode === 'ranking' ? fields.dtmaRanking ?? [] : fields.dai;
    return targetFields.some((item) => item.isNew);
  }
);

/**
 * [台別データ推移] テーブルのfieldsを取得する
 */
export const settingsOptionsDaiTransitionFieldsSelector = createSelector(
  settingsOptionsDaiSelector,
  (settingsOptionsDai) => {
    return settingsOptionsDai?.fields.transition;
  }
);

/**
 * 検索されたエリアのホール一覧を取得する
 */
export const settingsOptionsDaiSearchConditionHallsBySearchedAreaSelector =
  createSelector(
    [
      settingsOptionsDaiSearchConditionSelector,
      unitDataListReportsSearchConditionSelector,
    ],
    ({ halls, areas }, searchCondition) => {
      const selectedAreaIds = searchCondition.areas;
      if (!selectedAreaIds?.length) {
        return halls ?? [];
      }
      const hallCodesInAreas = areas
        .filter((area) => selectedAreaIds.includes(area.id))
        .flatMap((area) => area.halls);

      return halls.filter((hall) => hallCodesInAreas.includes(hall.code));
    }
  );

/**
 * [台別データ推移] テーブルのfieldsのisNewがtrueのものがあるかどうかを取得する
 */
export const settingsOptionsUnitDataListTransitionFieldsIsNewSelector =
  createSelector(
    settingsOptionsDaiTransitionFieldsSelector,
    (fieldsTransition) => {
      if (!fieldsTransition) {
        return false;
      }
      return fieldsTransition.some((item) => item.isNew);
    }
  );

// Reducer

export function settingsOptionsDaiReducer(
  state = initialState,
  action: SettingsOptionsDaiAction
): SettingsOptionsDaiState {
  switch (action.type) {
    case FETCH_SETTINGS_OPTIONS_DAI_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_SETTINGS_OPTIONS_DAI_SUCCESS:
      return {
        ...state,
        loadingState: 'loaded',
        settingsOptionsDai: action.payload.settingsOptionsDai,
      };
    case RENEW_SETTINGS_OPTIONS_DAI:
      return initialState;
    default:
      return state;
  }
}
